import type {
  SharedViewFailureReason,
  SharedViewFailureReasonKind,
  SharedViewFailureStage,
} from '@/utils/filter/sharedViewDiagnostics';

export type SharedViewFailureDiagnosisKind =
  | 'recordNotFound'
  | 'noDownloadLink'
  | 'accessDenied'
  | 'recordFileMissing'
  | 'viewFetchFailed'
  | 'downloadLinkInvalid'
  | 'downloadFailed'
  | 'payloadInvalid'
  | 'validationFailed'
  | 'unknown';

export interface SharedViewFailureDiagnosis {
  kind: SharedViewFailureDiagnosisKind;
  httpStatus?: number;
  errorCode?: string;
}

export interface SharedViewClassifiableFailure {
  stage: SharedViewFailureStage;
  httpStatus?: number;
  errorCode?: string;
  reasons: SharedViewFailureReason[];
  response?: { statusLabel?: string };
}

const NO_CONTENT_STATUS_LABEL = 'No Content';
const ACCESS_DENIED_STATUSES: number[] = [401, 403];
const RECORD_FILE_MISSING_STATUS = 404;
const INVALID_LINK_REASON_KINDS: readonly SharedViewFailureReasonKind[] = [
  'downloadLinkNotString',
  'downloadLinkMalformed',
  'downloadHostNotAllowed',
];

const classifyFetchFailure = (failure: SharedViewClassifiableFailure): SharedViewFailureDiagnosisKind => {
  if (failure.httpStatus !== undefined && ACCESS_DENIED_STATUSES.includes(failure.httpStatus)) {
    return 'accessDenied';
  }
  return failure.httpStatus === RECORD_FILE_MISSING_STATUS ? 'recordFileMissing' : 'viewFetchFailed';
};

const classifyDownloadUrlFailure = (failure: SharedViewClassifiableFailure): SharedViewFailureDiagnosisKind => {
  if (failure.reasons.some(reason => reason.kind === 'downloadLinkMissing')) {
    return failure.response?.statusLabel === NO_CONTENT_STATUS_LABEL ? 'recordNotFound' : 'noDownloadLink';
  }
  return failure.reasons.some(reason => INVALID_LINK_REASON_KINDS.includes(reason.kind))
    ? 'downloadLinkInvalid'
    : 'unknown';
};

const classifyKind = (failure: SharedViewClassifiableFailure): SharedViewFailureDiagnosisKind => {
  switch (failure.stage) {
    case 'fetchView':
      return classifyFetchFailure(failure);
    case 'invalidDownloadUrl':
      return classifyDownloadUrlFailure(failure);
    case 'downloadFilters':
      return 'downloadFailed';
    case 'invalidPayload':
      return 'payloadInvalid';
    case 'validation':
      return 'validationFailed';
    default:
      return 'unknown';
  }
};

export const classifySharedViewFailure = (failure: SharedViewClassifiableFailure): SharedViewFailureDiagnosis => ({
  kind: classifyKind(failure),
  httpStatus: failure.httpStatus,
  errorCode: failure.errorCode,
});
